// docs/64 village polish: the little things the Oryx sheets don't have — the dogs, cats and hens
// that wander a village, and what the inn puts on the table. Drawn like the encounter's wildlife
// (game/wildlife/sprites.ts): rows of palette letters, `.` clear, art facing east. Each picture is
// painted once into a canvas and kept as a data URL for CSS backgrounds.

export type CritterKind = 'dog' | 'cat' | 'hen';
export type DishKind = 'stew' | 'bread' | 'mug';

interface Art { pal: Record<string, string>; frames: readonly (readonly string[])[] }

const CRITTERS: Record<CritterKind, Art[]> = {
  dog: [
    { pal: { a: '#8a5a32', b: '#5a3a1e', n: '#2a1a10', e: '#1a1010', t: '#c89a6a' }, frames: [
      ['..........aa.', '.........aaab', 'b.......aaean', '.baaaaaaaaa..', '..aaaaaaaa...', '..aa.....aa..', '..a......a...'],
      ['..........aa.', '.........aaab', '.b......aaean', '.baaaaaaaaa..', '..aaaaaaaa...', '...aa...aa...', '...a.....a...'],
    ] },
    { pal: { a: '#d8c8a8', b: '#3a3026', n: '#2a2020', e: '#1a1010', t: '#3a3026' }, frames: [
      ['..........bb.', '.........aaab', 't.......aaean', '.taaaabbaaa..', '..aaabbaaa...', '..aa.....aa..', '..a......a...'],
      ['..........bb.', '.........aaab', '.t......aaean', '.taaaabbaaa..', '..aaabbaaa...', '...aa...aa...', '...a.....a...'],
    ] },
  ],
  cat: [
    { pal: { a: '#e08a3a', b: '#b0602a', e: '#2a3010' }, frames: [
      ['.........a.a', 'a........aaa', '.a.......aea', '.a.aaaaaaaa.', '..aababaab..', '..a.a..a.a..'],
      ['.........a.a', '.a.......aaa', 'a........aea', '.a.aaaaaaaa.', '..aababaab..', '...a.a.a.a..'],
    ] },
    { pal: { a: '#3a3a44', b: '#24242c', e: '#c8d040' }, frames: [
      ['.........a.a', 'a........aaa', '.a.......aea', '.a.aaaaaaaa.', '..aababaab..', '..a.a..a.a..'],
      ['.........a.a', '.a.......aaa', 'a........aea', '.a.aaaaaaaa.', '..aababaab..', '...a.a.a.a..'],
    ] },
  ],
  hen: [
    { pal: { a: '#f2ece0', b: '#c8bca8', r: '#d83a2a', y: '#e8a830', e: '#1a1010' }, frames: [
      ['.....r..', '....aar.', '....aeay', 'b...aaa.', 'bbaaaaa.', '.aaaaba.', '..aaaa..', '...y.y..'],
      ['........', '.....r..', '....aar.', 'b...aeay', 'bbaaaaa.', '.aaaaba.', '..aaaa..', '..y..y..'],
    ] },
    { pal: { a: '#a8642e', b: '#6e3e1a', r: '#d83a2a', y: '#e8a830', e: '#1a1010' }, frames: [
      ['.....r..', '....aar.', '....aeay', 'b...aaa.', 'bbaaaaa.', '.aaaaba.', '..aaaa..', '...y.y..'],
      ['........', '.....r..', '....aar.', 'b...aeay', 'bbaaaaa.', '.aaaaba.', '..aaaa..', '..y..y..'],
    ] },
  ],
};

/** What the inn serves, full to empty: a bowl of stew, a loaf on a board, a mug of ale. */
const DISHES: Record<DishKind, Art> = {
  stew: { pal: { w: '#e8e0d0', s: '#9a8a78', b: '#8a4a22', c: '#c8783a', g: '#6a9a3a', k: '#4a3a2a' }, frames: [
    ['..........', '.wwwwwwww.', 'wbcbgbcbbw', '.wbbcbgbw.', '..wwwwww..', '...kkkk...'],
    ['..........', '.wwwwwwww.', 'wsbbsbsssw', '.wsbsssbw.', '..wwwwww..', '...kkkk...'],
    ['..........', '.wwwwwwww.', 'wssssssssw', '.wsssssw..', '..wwwwww..', '...kkkk...'],
  ] },
  bread: { pal: { a: '#d89a4a', b: '#a8682a', c: '#f0c880', w: '#8a5a32', h: '#e8b85a' }, frames: [
    ['...cccc...', '..caaaac..', '.caabaaac.', 'haaaaaaaah', 'wwwwwwwwww', '.w......w.'],
    ['..........', '.....ccc..', '....caaac.', '....aaaaah', 'wwwwwwwwww', '.w......w.'],
    ['..........', '..........', '..........', '......c.c.', 'wwwwwwwwww', '.w......w.'],
  ] },
  mug: { pal: { f: '#f4f0e0', a: '#d8a030', w: '#8a5a32', d: '#5a3a1e' }, frames: [
    ['.ffff.', 'faaaaf', 'wwwwwd', 'wwwwwd', 'wwwwww', '.wwww.'],
    ['......', 'w....w', 'waaaaw', 'wwwwwd', 'wwwwww', '.wwww.'],
    ['......', 'w....w', 'w....w', 'wwwwwd', 'wwwwww', '.wwww.'],
  ] },
};

function paint(rows: readonly string[], pal: Record<string, string>): { url: string; w: number; h: number } {
  const h = rows.length, w = Math.max(...rows.map((r) => r.length));
  if (typeof document === 'undefined') return { url: '', w, h };
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const x = c.getContext('2d');
  if (!x) return { url: '', w, h };
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = pal[row[i]!]; if (col) { x.fillStyle = col; x.fillRect(i, j, 1, 1); } } });
  return { url: c.toDataURL(), w, h };
}

const cache = new Map<string, { url: string; w: number; h: number }>();
const once = (k: string, make: () => { url: string; w: number; h: number }) => { let v = cache.get(k); if (!v) { v = make(); cache.set(k, v); } return v; };

/** A critter's picture: kind, coat (wraps), frame 0/1. */
export function critterArt(kind: CritterKind, coat: number, frame: number): { url: string; w: number; h: number } {
  const arts = CRITTERS[kind];
  const a = arts[((coat % arts.length) + arts.length) % arts.length]!;
  return once(`${kind}:${coat % arts.length}:${frame % 2}`, () => paint(a.frames[frame % 2]!, a.pal));
}

/** A dish's picture: 0 full, 1 half eaten, 2 finished. */
export function dishArt(kind: DishKind, left: 0 | 1 | 2): { url: string; w: number; h: number } {
  return once(`dish:${kind}:${left}`, () => paint(DISHES[kind].frames[left]!, DISHES[kind].pal));
}

/** How fast each kind ambles (cells a second). */
export const CRITTER_PACE: Record<CritterKind, number> = { dog: 1.3, cat: 0.8, hen: 0.5 };
